<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Ref } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import type { ClosingShiftForm } from '~/stores/shared/shift'
import type { Shift } from '~/types/db/shifts'

interface DialogData {
  businessId?: string
  shift?: Shift
}

interface DialogRef {
  close: () => void
  data?: DialogData
}

const { t } = useNuxtApp().$i18n
const dialogRef = inject<Ref<DialogRef>>('dialogRef')

// Loading state
const loading = ref(true)
const initError = ref<string | null>(null)

// Access data through dialogRef.value.data (PrimeVue pattern)
const dialogBusinessId = computed(() => dialogRef?.value?.data?.businessId)
const dialogShift = computed(() => dialogRef?.value?.data?.shift)
const routeBusinessId = useRouteParam('buuid') as string

const businessId = dialogBusinessId.value || routeBusinessId
const shiftData = dialogShift.value

// Store and refs (will be initialized in onMounted)
let shiftStore: ReturnType<typeof useShiftStore>

// Reactive refs from store
const shift = ref<Shift | undefined>()
const totalSales = ref<number | undefined>()
const totalExpenses = ref<number | undefined>()
const closingCashAmount = ref<string | undefined>()
const permissions = ref<Record<string, boolean>>({})

// Initialize store on mount
onMounted(async () => {
  try {
    // Validate required data
    if (!businessId) {
      throw new Error('Business ID is required to close shift dialog')
    }

    if (!shiftData?.id) {
      throw new Error('Shift data is required to close shift dialog')
    }

    // Initialize store with factory - it will load the shift data
    shiftStore = await createShiftStore(businessId, shiftData.id.toString())

    // Use storeToRefs for reactivity with destructuring
    const { shift: storeShift, totalSales: storeTotalSales, totalExpenses: storeTotalExpenses, closingCashAmount: storeClosingCashAmount, permissions: storePermissions } = storeToRefs(shiftStore)

    // Watch store changes and update local refs
    watchEffect(() => {
      shift.value = storeShift.value
      totalSales.value = storeTotalSales.value
      totalExpenses.value = storeTotalExpenses.value
      closingCashAmount.value = storeClosingCashAmount.value
      permissions.value = storePermissions.value
    })
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Failed to initialize close shift form')
    initError.value = errorMessage
    useErrorToast(errorMessage)
  }
  finally {
    loading.value = false
  }
})

async function submitWrapper(formData: ClosingShiftForm) {
  if (!formData || !shiftData?.id || !shiftStore) {
    useErrorToast('Invalid form data')
    return
  }

  try {
    await shiftStore.closeShift(formData)
    useSuccessToast('Shift closed successfully')
    dialogRef?.value?.close()
    navigateTo('/store/pos')
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : t('Failed to close shift')
    useErrorToast(errorMessage)
  }
}
</script>

<template>
  <!-- Loading State -->
  
<FormKitLazyProvider config-file="true">
<div
    v-if="loading"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <ProgressSpinner size="50px" />
      <p class="mt-4 text-gray-600">
        {{ t('Loading shift data') }}...
      </p>
    </div>
  </div>

  <!-- Error State -->
  <div
    v-else-if="initError"
    class="flex justify-center items-center p-8"
  >
    <div class="text-center">
      <i class="pi pi-exclamation-triangle text-red-500 text-4xl mb-4" />
      <p class="text-red-600">
        {{ initError }}
      </p>
    </div>
  </div>

  <!-- Form Content -->
  <div
    v-else
    class="flex gap-3 w-full"
  >
    <div class="w-1/2">
      <h3 class="my-1">
        {{ t('Shift') }}
        : {{ shiftStore?.shiftId }}
      </h3>
      <FormKit
        type="text"
        disabled
        :label="t('Status')"
        :value="shiftStore?.shift?.status ? t(`${shiftStore.shift.status}`) : ''"
        placeholder="0.00"
        name="status"
      />
      <FormKit
        type="text"
        disabled
        :label="t('Opening cash amount')"
        :value="String(shiftStore?.shift?.opening_cash_amount ?? 0)"
        placeholder="0.00"
        name="openning_cash_amount"
      />
      <FormKit
        v-if="shift?.notes"
        type="textarea"
        disabled
        :label="t('Notes')"
        :value="shift?.notes ?? ''"
        name="notes"
      />
      <FormKit
        id="order-details"
        class="flex"
        type="form"
        :actions="false"
        @submit="submitWrapper"
      >
        <FormKit
          type="hidden"
          :label="t('ID')"
          name="id"
          :value="String(shiftStore?.shiftId)"
        />
        <FormKit
          type="textarea"
          cols="20"
          :label="t('Closing notes')"
          name="closing_notes"
        />
        <FormKit
          v-model="closingCashAmount"
          type="text"
          :label="t('Closing cash amount')"
          placeholder="0.00"
          name="closing_cash_amount"
          validation="required|number|decimal:2"
          validation-visibility="live"
        />
        <FormKit
          type="submit"
          :label="t('Close shift')"
        />
      </FormKit>
    </div>
    <div
      v-if="permissions?.can_view_shift_financials"
      class="w-1/2"
    >
      <div
        v-if="shift?.opening_cash_amount"
        class="my-4"
      >
        <label class="font-semibold text-sm">{{ t('Open shift amount') }}</label>
        <div>${{ priceFormatter(shift.opening_cash_amount) }}</div>
      </div>
      <div
        v-if="totalSales"
        class="my-4"
      >
        <label class="font-semibold text-sm">{{ t('Total sales') }} {{ t('Cash') }}</label>
        <div>${{ priceFormatter(totalSales) }}</div>
      </div>
      <div
        v-if="totalExpenses"
        class="my-4"
      >
        <label class="font-semibold text-sm">{{ t('Total expenses') }}</label>
        <div>(-) ${{ priceFormatter(totalExpenses) }}</div>
      </div>
      <div
        v-if="shiftStore?.computedExpectedCashAmount"
        class="my-4"
      >
        <label class="font-semibold text-sm">{{ t('Expected theoric cash') }}</label>
        <div>${{ priceFormatter(shiftStore.computedExpectedCashAmount) }}</div>
      </div>
    </div>
  </div>
</FormKitLazyProvider>

</template>
